// Keeps the win-amount tooltip inside the campaign card's bounds instead of letting its
// percent-anchored CSS position push it past the card's left/right edge. Rather than
// re-positioning the tooltip directly, it measures the tooltip against its closest campaign
// card ancestor and nudges it back via a `--tooltip-clamp-offset` CSS var (consumed by a
// `translateX` in the tooltip's CSS) whenever it would overflow. Recomputed on mount and
// whenever the fill container/card resize, the window resizes, or the page scrolls (including
// non-bubbling scroll on the horizontally scrolling carousel container).
//
// The clamp is intentionally skippable via `cardOverflowIsPossible` — callers pass `false` to let the
// tooltip overflow the card (e.g. on the level that is not the highest one), in which
// case the offset is simply reset to 0 (natural percent-anchored position).
import { useLayoutEffect, useRef, type RefObject } from "react";

import cardStyles from "../../../shared/CampaignCard/CampaignCardStyles.module.css";

const TOOLTIP_CARD_MARGIN = 2;

const clampTooltipToCard = (tooltip: HTMLSpanElement, card: Element, cardOverflowIsPossible: boolean): void => {
    if (!cardOverflowIsPossible) {
        tooltip.style.setProperty('--tooltip-clamp-offset', '0px');
        return;
    }

    tooltip.style.setProperty('--tooltip-clamp-offset', '0px');
    const tooltipRect = tooltip.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    let offset = 0;
    if (tooltipRect.left < cardRect.left + TOOLTIP_CARD_MARGIN) {
        offset = cardRect.left + TOOLTIP_CARD_MARGIN - tooltipRect.left;
    } else if (tooltipRect.right > cardRect.right - TOOLTIP_CARD_MARGIN) {
        offset = cardRect.right - TOOLTIP_CARD_MARGIN - tooltipRect.right;
    }

    tooltip.style.setProperty('--tooltip-clamp-offset', `${offset}px`);
};

export const useClampTooltipToCard = (cardOverflowIsPossible: boolean): RefObject<HTMLSpanElement> => {
    const tooltipRef = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const tooltip = tooltipRef.current;
        const fillContainer = tooltip?.parentElement;
        const card = tooltip?.closest(`.${cardStyles['campaignCardContainer']}`);
        if (!tooltip || !fillContainer || !card) return;

        const recalculate = (): void => clampTooltipToCard(tooltip, card, cardOverflowIsPossible);

        recalculate();

        const resizeObserver = new ResizeObserver(recalculate);
        resizeObserver.observe(fillContainer);
        resizeObserver.observe(card);

        window.addEventListener('resize', recalculate);
        // capture: true so scroll events from the horizontally scrolling carousel container
        // (which don't bubble) are still caught here.
        window.addEventListener('scroll', recalculate, { capture: true, passive: true });

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', recalculate);
            window.removeEventListener('scroll', recalculate, { capture: true });
        };
    }, [cardOverflowIsPossible]);

    return tooltipRef;
};
